import { Component, OnInit, Input, NgModule, Host, Optional, ViewEncapsulation, ViewChild, OnDestroy } from '@angular/core';
import { InfoboxService } from '@app/services/infobox.service';
import { Observable, combineLatest, Subscription, of } from 'rxjs';
import { InfoB, InfoBoxWidth } from '@app/models/infobox';
import { CommonModule } from '@angular/common';
import { HideParentIfEmptyDirective } from '@app/directives/hide-parent-if-empty.directive';
import { tap, mergeMap, map } from 'rxjs/operators';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { SecurityService } from '@app/services/security.service';
import { Roles } from '@app/common/roles';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { SortablejsModule, SortablejsDirective } from 'ngx-sortablejs';
import { Options, SortableEvent } from 'sortablejs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Utilities } from '@app/common/utilities';
import { AlertTemplatesComponent } from '@app/components/alert-templates/alert-templates.component';
import { ConfirmDialogComponent } from '@app/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { NgSubscribeModule } from '@app/directives/ng-subscribe.directive';
import { InfoBoxUtilities } from '@app/components/infobox-zone/infobox-utility';
import { InfoboxModule } from '@app/components/infobox-zone/infobox/infobox.component';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';

@Component({
  selector: 'app-infobox-zone',
  templateUrl: './infobox-zone.component.html',
  styleUrls: ['./infobox-zone.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class InfoboxZoneComponent implements OnInit, OnDestroy {

  @Input() area: string;
  @Input() textAlignmentClass: string;
  boxes$: Observable<Array<InfoB>>;
  subscription = new Subscription();
  featureSettings = this.featureSettingsStoreService.state;
  hasUserPermissionToEdit$: Observable<boolean>;
  options: Options;
  editMode$ = this.service.currentEditMode$;
  @ViewChild(SortablejsDirective) sortable: SortablejsDirective;

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private service: InfoboxService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private securityService: SecurityService,
    private featureSettingsStoreService: FeatureSettingsStoreService,

    @Host() @Optional() public hideParentIfEmptyDirective: HideParentIfEmptyDirective) {
    this.options = {
      disabled: false,
      filter: '.ignore-elements',
      handle: '.move-icon',
      onUpdate: (event: SortableEvent) => {
        this.onSortUpdate();
      }
    };
  }

  get routerUrl() {
    return this.router.url;
  }

  get isAreaHero() {
    return this.area.toLowerCase().indexOf('hero') > -1;
  }

  ngOnInit(): void {
    this.hasUserPermissionToEdit$ = this.securityService.hasUserAnyAppRole([Roles.Admin, Roles.Editor]);
    const activatedRouteForPrimaryOutlet = this.activatedRoute.root.children.find(_ => _.outlet === 'primary');

    this.boxes$ =
      combineLatest([activatedRouteForPrimaryOutlet.url, this.service.refresh$, this.service.currentEditMode$])
        .pipe(
          mergeMap(([url, , currentEditMode]) => {
            if (currentEditMode) {
              return this.service.getActiveAndFutureInfoBoxes('/' + url.join('/'), this.area)
                .pipe(
                  map(boxes => {
                    return boxes
                      .filter(b => !b.Hidden);
                  }),
                  tap(this.hideParentElement(currentEditMode))
                );
            } else {
              return this.service.getMyActiveInfoBoxes('/' + url.join('/'), this.area)
                .pipe(
                  tap(this.hideParentElement(currentEditMode))
                );
            }
          })
        );
  }

  private hideParentElement(currentEditMode): (x: InfoB[]) => void {
    return (boxes) => {
      if (currentEditMode) {
        this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.showElement();
      } else {
        if (boxes.length === 0) {
          this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.hideElement();
        } else {
          this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.showElement();
        }
      }
    };
  }

  private onSortUpdate() {
    const boxesToUpdate = (this.sortable.sortablejs as Array<InfoB>).map((box, index) => {
      box.Order = index;
      return box;
    });

    this.subscription.add(
      this.service.updateInfoboxes(boxesToUpdate)
        .subscribe(() => {
          Utilities.openSnackBar(this.snackBar, AlertTemplatesComponent, AlertTemplatesComponent.updatedorder);
        }));
  }

  setSize(size: InfoBoxWidth, box: InfoB) {
    box.Width = size;
    this.save(box);
  }

  save(box: InfoB) {
    this.subscription.add(
      this.service.updateInfobox(box.Id, box)
        .subscribe(() => {
          Utilities.openSnackBar(this.snackBar, AlertTemplatesComponent, AlertTemplatesComponent.genericupdated);
        }));
  }

  duplicate(box: InfoB) {
    box.Id = undefined;
    this.subscription.add(
      this.service.addInfobox(box)
        .subscribe(() => {
          Utilities.openSnackBar(this.snackBar, AlertTemplatesComponent, AlertTemplatesComponent.genericadded);
        }));
  }

  hide(box: InfoB) {
    this.subscription.add(
      this.service.hideInfobox(box.Id)
        .subscribe());
  }

  remove(box: InfoB) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent);
    this.subscription.add(
      dialogRef.afterClosed()
        .pipe(mergeMap(result => {
          if (result) {
            return this.service.deleteInfobox(box.Id);
          } else {
            return of(null);
          }
        }))
        .subscribe(result => {
          if (result) {
            Utilities.openSnackBar(this.snackBar, AlertTemplatesComponent, AlertTemplatesComponent.genericdeleted);
          }
        }));
  }

  getWidthClasses(box: InfoB) {
    return InfoBoxUtilities.getWidthClasses(box);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

}
@NgModule({
  declarations: [InfoboxZoneComponent],
  exports: [InfoboxZoneComponent],
  imports: [
    CommonModule,
    MatSlideToggleModule,
    MatButtonModule,
    RouterModule,
    MatMenuModule,
    SortablejsModule,
    NgSubscribeModule,
    WidgetPlaceholderModule,
    InfoboxModule
  ]
})
export class InfoboxZoneModule { }
